import {
  Box,
  createStyles,
  Drawer,
  Flex,
  Group,
  Progress,
  Text,
} from "@mantine/core";
import React from "react";
import { IconBuildingHospital } from "@tabler/icons-react";

const useStyles = createStyles(() => ({
  drawer: {
    ".mantine-Drawer-content": {
      backgroundColor: "#f5f5f5",
    },
    ".mantine-Drawer-title": {
      fontSize: 20,
      fontWeight: 700,
      fontFamily: "Inter",
      color: "#000",
      width: "100%",
    },
    ".mantine-Drawer-header": {
      backgroundColor: "#fff",
    },
  },
  container: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    padding: "16px",
    overflowY: "auto",
    scrollbarWidth: "thin",
    scrollbarColor: "#E5E5E5",
    "::-webkit-scrollbar": {
      width: "10px",
    },
    "::-webkit-scrollbar-track": {
      background: "#f0f0f0",
      borderRadius: "10px",
    },
    "::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "10px",
    },
    "::-webkit-scrollbar-thumb:hover": {
      background: "#E5E5E5",
    },
  },
  facilityRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "14px 0",
    borderBottom: "1px solid #F3F4F6",
    "&:last-child": { borderBottom: "none" },
  },
  statBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    fontFamily: "Inter",
  },
}));

const getFacilityDotColor = (status) => {
  switch (status) {
    case "good": return "#10B981";
    case "warning": return "#F59E0B";
    case "critical": return "#EF4444";
    default: return "#9CA3AF";
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case "good": return "Compliant";
    case "warning": return "Warning";
    case "critical": return "Critical";
    default: return "Unknown";
  }
};

const getStatusBg = (status) => {
  switch (status) {
    case "good": return "#ECFDF5";
    case "warning": return "#FFFBEB";
    case "critical": return "#FEF2F2";
    default: return "#F3F4F6";
  }
};

const FacilityHealthDrawer = ({ opened, close, facilities }) => {
  const { classes } = useStyles();

  return (
    <Drawer
      size="35%"
      offset={8}
      radius="md"
      zIndex={1000}
      position="right"
      title={
        <Group spacing={10}>
          <IconBuildingHospital size={22} color="#6366F1" />
          <Text ff="Inter" fw={700} fz={18} color="#1F2937">
            Facility Health ({facilities?.length || 0})
          </Text>
        </Group>
      }
      opened={opened}
      className={classes.drawer}
      onClose={close}
      overlayProps={{ backgroundOpacity: 2.5, blur: 4 }}
    >
      <Box className={classes.container}>
        {facilities?.length > 0 ? (
          facilities.map((fh) => (
            <Box key={fh.id} className={classes.facilityRow}>
              <Box style={{
                width: 12, height: 12, borderRadius: "50%",
                backgroundColor: getFacilityDotColor(fh.status),
                flexShrink: 0,
              }} />
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Flex justify="space-between" align="center" mb={4}>
                  <Text ff="Inter" fz={14} fw={600} color="#374151" lineClamp={1} style={{ flex: 1 }}>
                    {fh.name}
                  </Text>
                  <Text ff="Inter" fz={14} fw={700} color="#374151" ml={8}>
                    {fh.score}%
                  </Text>
                </Flex>
                <Progress
                  value={fh.score}
                  color={fh.status === "good" ? "green" : fh.status === "warning" ? "yellow" : "red"}
                  size="sm"
                  radius="xl"
                />
                <Flex gap={12} mt={6} wrap="wrap">
                  <Text
                    className={classes.statBadge}
                    style={{ backgroundColor: getStatusBg(fh.status), color: getFacilityDotColor(fh.status) }}
                  >
                    {getStatusLabel(fh.status)}
                  </Text>
                  <Text ff="Inter" fz={11} color="#6B7280">
                    {fh.compliant}/{fh.total} compliant
                  </Text>
                  {fh.expired > 0 && (
                    <Text ff="Inter" fz={11} color="#EF4444" fw={500}>
                      {fh.expired} expired
                    </Text>
                  )}
                  {fh.expiring > 0 && (
                    <Text ff="Inter" fz={11} color="#F59E0B" fw={500}>
                      {fh.expiring} expiring
                    </Text>
                  )}
                  {fh.missing > 0 && (
                    <Text ff="Inter" fz={11} color="#9CA3AF" fw={500}>
                      {fh.missing} missing
                    </Text>
                  )}
                </Flex>
              </Box>
            </Box>
          ))
        ) : (
          <Flex align="center" justify="center" py={60}>
            <Text ff="Inter" fz={14} color="#9CA3AF">No facilities configured</Text>
          </Flex>
        )}
      </Box>
    </Drawer>
  );
};

export default FacilityHealthDrawer;
